/**
 * Canned response picker — a button in the composer that opens a searchable
 * list; picking one inserts its HTML into the draft (append, not replace —
 * an agent may have already typed a greeting).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { cannedResponsesApi } from './api';
import { Input } from '../../components/ui/input';
import { qk } from '../../lib/queryClient';

export function CannedResponsePicker({ onInsert }: { onInsert: (bodyHtml: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data } = useQuery({
    queryKey: qk.cannedResponses,
    queryFn: cannedResponsesApi.list,
    enabled: open,
    staleTime: 60_000,
  });

  const entries = (data?.entries ?? []).filter((c) =>
    query.trim() ? c.title.toLowerCase().includes(query.trim().toLowerCase()) : true,
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <FileText className="h-3.5 w-3.5" /> Canned response
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="fixed inset-x-2 bottom-24 z-20 max-h-[55vh] overflow-y-auto rounded-md border bg-popover p-2 shadow-md sm:absolute sm:inset-x-auto sm:bottom-full sm:left-0 sm:mb-1 sm:max-h-none sm:w-72 sm:overflow-visible">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="mb-1.5"
            />
            <div className="max-h-56 space-y-0.5 overflow-y-auto">
              {entries.length === 0 && (
                <p className="px-1 py-2 text-xs text-muted-foreground">No canned responses yet.</p>
              )}
              {entries.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    onInsert(c.bodyHtml);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="block w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                >
                  {c.folder ? <span className="text-muted-foreground">{c.folder} / </span> : null}
                  {c.title}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
